/* =====================================================================
   tower/driver/src/i18n.js — the handheld's language layer (driver handheld P1, P-10…P-13).

   One catalog per locale (tower/i18n/<locale>.json, flat keys), a reactive t(), and locale-aware
   formatters. Switching language is INSTANT and loses nothing: setLocale() swaps the catalog and tells
   every subscriber, and the driver screens re-render from their own state (feature.js
   _drvRelocalize) — entered quantities, the open sheet, focus and scroll all survive.

   Rules this file keeps:
     • a key missing in a locale falls back to English, and is RECORDED (missingKeys) so the coverage
       harness can fail on it — never a blank, never the raw key shown to a driver if English has it;
     • plurals are {"one": "...", "other": "..."} objects chosen by params.n;
     • {name} placeholders are filled from params as given — callers escape user text exactly as they
       did before (a catalog string is ours and may carry <b>);
     • time is the viewer's own clock, 12-hour, never UTC, never a zone label (time.js does the clock;
       this only words AM/PM the way the locale does: "2:14 PM" / "2:14 p. m.").
   ===================================================================== */
import en from '../../i18n/en.json';
import es from '../../i18n/es.json';
import index from '../../i18n/index.json';

const CATALOGS = { en, es };
const LKEY = 'routingapp:driver:locale';
const TAGS = { en: 'en-US', es: 'es-US' };   // US companies: dollars, US dates, in the driver's language

const _missing = new Set();
const _subs = new Set();
let _locale = 'en';

/** Every locale the platform ships a catalog for (index.json), as [{code, name}]. */
export function catalogLocales() { return (index.locales || []).filter((l) => CATALOGS[l.code]); }

/** The platform list narrowed by what this company offers (from /api/driver/me), never empty. */
export function offeredLocales() {
  const all = catalogLocales();
  const tenant = Array.isArray(window.DRV_TENANT_LOCALES) ? window.DRV_TENANT_LOCALES : null;
  const out = tenant ? all.filter((l) => tenant.indexOf(l.code) >= 0) : all;
  return out.length ? out : all.filter((l) => l.code === 'en');
}

export function locale() { return _locale; }
export function localeTag() { return TAGS[_locale] || 'en-US'; }

function _norm(l) {
  const s = String(l || '').trim().toLowerCase().split(/[-_]/)[0];
  return CATALOGS[s] ? s : null;
}

function _pick(v, params) {
  if (v && typeof v === 'object') {
    const n = params && typeof params.n === 'number' ? params.n : null;
    // Spanish and English both use one/other; a locale with more forms adds keys, not code.
    return (n === 1 && v.one != null) ? v.one : (v.other != null ? v.other : v.one);
  }
  return v;
}

/** The string for `key` in the current locale, with {param} placeholders filled. */
export function t(key, params) {
  let v = CATALOGS[_locale] && CATALOGS[_locale][key];
  if (v == null) {
    if (_locale !== 'en') _missing.add(_locale + ':' + key);
    v = en[key];
  }
  if (v == null) { _missing.add('en:' + key); return key; }
  let s = String(_pick(v, params));
  if (params) s = s.replace(/\{(\w+)\}/g, (m, k) => (params[k] != null ? String(params[k]) : m));
  return s;
}

/** Whether the platform has words for this key (for tenant data that MAY have a translation). */
export function hasKey(key) { return Object.prototype.hasOwnProperty.call(en, key); }

/** Keys asked for that a catalog did not have — read by tools/check-i18n.mjs. */
export function missingKeys() { return [..._missing]; }

/** Subscribe to language changes; returns the unsubscribe. */
export function onLocale(fn) { _subs.add(fn); return () => _subs.delete(fn); }

/**
 * Switch language. `opts.persist` (default true) remembers it on this phone; `opts.source` is passed to
 * subscribers so the app can tell a driver's own tap (which is written to the office record) from the
 * office's value arriving on sign-in (which is not written back).
 */
export function setLocale(l, opts) {
  opts = opts || {};
  const next = _norm(l);
  if (!next) return false;
  const changed = next !== _locale;
  _locale = next;
  if (opts.persist !== false) { try { localStorage.setItem(LKEY, next); } catch (e) {} }
  if (changed) _subs.forEach((fn) => { try { fn(next, opts.source || 'app'); } catch (e) { /* i18n-exempt: developer console */ console.warn('[i18n] subscriber failed', e); } });
  return changed;
}

/** The language this phone last used (before /me answers, and offline). */
export function storedLocale() {
  try { return _norm(localStorage.getItem(LKEY)); } catch (e) { return null; }
}

// ── formatters ───────────────────────────────────────────────────────────────────────────────
const _nf = {};
function _num(opts) { const k = _locale + JSON.stringify(opts || {}); return _nf[k] || (_nf[k] = new Intl.NumberFormat(localeTag(), opts)); }

/** Money, in dollars, the locale's way: "$1,234.50" (both locales use the US convention for USD). */
export function fmtMoney(x) {
  const n = Number(x);
  return _num({ style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(isFinite(n) ? n : 0);
}
/** A count: "1,234". */
export function fmtNumber(x, digits) {
  const n = Number(x);
  if (!isFinite(n)) return '—';
  return _num(digits != null ? { minimumFractionDigits: digits, maximumFractionDigits: digits } : {}).format(n);
}

function _ampm(s) {
  if (_locale !== 'es' || !s) return s;
  return String(s).replace(/\bAM\b/g, 'a. m.').replace(/\bPM\b/g, 'p. m.');
}
/** A time of day on the viewer's clock, 12-hour (time.js fmtTime), worded for the locale. */
export function fmtTimeL(x, opts) { return _ampm(typeof fmtTime === 'function' ? fmtTime(x, opts) : ''); }
/** A window "8 AM–12 PM" (time.js fmtWin), worded for the locale. */
export function fmtWinL(w) { return _ampm(typeof fmtWin === 'function' ? fmtWin(w) : w); }
/** "ETA 16:10" style free text with clock times in it (time.js fmtClockText). */
export function fmtClockTextL(s) { return _ampm(typeof fmtClockText === 'function' ? fmtClockText(s) : s); }

const _df = {};
/** A calendar day in the locale ("Oct 14, 2026" / "14 oct 2026"); a YYYY-MM-DD day is never shifted. */
export function fmtDateL(x, opts) {
  opts = opts || {};
  if (_locale === 'en' && !opts.long && typeof fmtDate === 'function') return fmtDate(x, opts);
  if (x == null || x === '') return '';
  // long: a heading — "Thursday, October 15" / "jueves, 15 de octubre"
  const o = opts.long ? { weekday: 'long', month: 'long', day: 'numeric' } : { month: 'short', day: 'numeric' };
  if (!opts.short && !opts.long) o.year = 'numeric';
  if (opts.weekday && !opts.long) o.weekday = 'short';
  let tz, d;
  const m = typeof x === 'string' && x.match(/^\s*(\d{4})-(\d{2})-(\d{2})\s*$/);
  if (m) { d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])); tz = 'UTC'; }   // a day, not a moment
  else {
    d = x instanceof Date ? x : new Date(x);
    if (isNaN(d)) return typeof x === 'string' ? x : '';
    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch (e) { tz = undefined; }
  }
  const k = localeTag() + '|' + tz + '|' + JSON.stringify(o);
  const f = _df[k] || (_df[k] = new Intl.DateTimeFormat(localeTag(), Object.assign(tz ? { timeZone: tz } : {}, o)));
  return f.format(d);
}
/** A moment: day + time, the locale's way, on the viewer's clock. */
export function fmtDateTimeL(x) {
  const d = x instanceof Date ? x : new Date(x);
  if (isNaN(d)) return typeof x === 'string' ? x : '';
  return fmtDateL(d) + ', ' + fmtTimeL(d);
}

// Start in what this phone last used; /api/driver/me corrects it on sign-in. Nothing chosen on this phone yet (a new
// install, the Home Screen app's own storage): the phone's own language when we have it (es → Spanish), else English.
// Not stored: only a real choice, or the driver's record, is remembered.
{ const s = storedLocale(); let p = null; try { p = _norm((navigator.languages && navigator.languages[0]) || navigator.language); } catch (e) {}
  if (s) _locale = s; else if (p) _locale = p; }

/* Exposed for the classic scripts that are part of the handheld (driversync.js, the sign-in card,
   the ticker) and for the harness. One layer, one catalog — never a second copy of the strings. */
Object.assign(window, {
  drvT: t, drvHasKey: hasKey, drvLocale: locale, drvSetLocale: setLocale, drvOnLocale: onLocale,
  drvLocales: offeredLocales, drvCatalogLocales: catalogLocales, drvI18nMissing: missingKeys,
  drvFmtMoney: fmtMoney, drvFmtNumber: fmtNumber, drvFmtTime: fmtTimeL, drvFmtDate: fmtDateL,
  drvFmtWin: fmtWinL, drvFmtDateTime: fmtDateTimeL,
  __drvCatalogs: CATALOGS,
});
